/**
 * React Query mutation hooks for coverage and RFP data modifications
 *
 * These hooks handle updates, creates, and deletes for coverages and RFPs
 * with direct cache updates to avoid unnecessary refetches.
 *
 * Error Handling:
 * - All mutations include default error notification handling
 * - Errors are displayed as toast notifications requiring manual dismissal
 * - Error messages are extracted from the backend AppError structure
 */

import { useNotification } from "@platform/hooks";
import { extractErrorMessage } from "@platform/api";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { rateQueryKeys } from "@platform/btx-rates";
import {
  BatchUpdateCoverageRequest,
  BatchUpdateCoverageResponse,
  RfpNotePayload,
  SaveRfpRequest,
  SaveRfpResponse,
  batchUpdateCoverage,
  downloadDocumentFile,
  saveRfp,
  transformSingleCoverage,
  updateCoverage,
  upsertCoverageNote,
  upsertRfpNote,
} from "@platform/plan-grid-core";
import { queryKeys, useApiClient } from "@platform/plan-grid-core";

import {
  updateCoverageInCache,
  updateRfpCoveragesInCache,
} from "./cacheHelpers";

/**
 * Hook to update a single coverage
 *
 * Updates the query cache directly on success to avoid refetching.
 * Displays error toast notifications on failure requiring manual dismissal.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function CoverageEditor() {
 *   const mutation = useUpdateCoverage({
 *     onSuccess: () => {
 *       console.log("Coverage updated!");
 *     }
 *   });
 *
 *   const handleSave = () => {
 *     mutation.mutate({
 *       coverageId: "cov-123",
 *       coverageType: "dental",
 *       updates: [...]
 *     });
 *   };
 *
 *   return (
 *     <button onClick={handleSave} disabled={mutation.isPending}>
 *       {mutation.isPending ? "Saving..." : "Save"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useUpdateCoverage = (
  options?: UseMutationOptions<
    BatchUpdateCoverageResponse,
    Error,
    BatchUpdateCoverageRequest
  >,
): UseMutationResult<
  BatchUpdateCoverageResponse,
  Error,
  BatchUpdateCoverageRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { showError } = useNotification();

  // Extract custom onError to compose with default error handling
  const { onError: customOnError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (request: BatchUpdateCoverageRequest) =>
      updateCoverage(request, axiosInstance),
    onSuccess: (response, variables) => {
      // Update cache directly instead of invalidating
      if (response.coverage && variables.coverageType) {
        // Transform coverage from backend format to frontend format before caching
        const transformedCoverage = transformSingleCoverage(response.coverage);
        updateCoverageInCache(
          queryClient,
          variables.coverageId,
          variables.coverageType,
          transformedCoverage,
        );
      }
    },
    onError: (error, variables, onMutateResult, context) => {
      const errorMessage = extractErrorMessage(error);
      showError(errorMessage, false); // false = requires manual dismissal
      if (customOnError) {
        customOnError(error, variables, onMutateResult, context);
      }
    },
    ...restOptions,
  });
};

/**
 * Hook to batch update multiple cells in a coverage
 *
 * This is semantically the same as useUpdateCoverage but named differently
 * for clarity when used in batch update contexts.
 * Displays error toast notifications on failure requiring manual dismissal.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function BatchCellEditor() {
 *   const mutation = useBatchUpdateCoverage();
 *
 *   const handleBatchSave = () => {
 *     mutation.mutate({
 *       coverageId: "cov-123",
 *       coverageType: "dental",
 *       updates: [
 *         { path: "plan1.IN_NETWORK", value: "$1500", benefitKey: "annualMaximum" },
 *         { path: "plan1.OUT_OF_NETWORK", value: "$1000", benefitKey: "annualMaximum" },
 *         { path: "plan2.IN_NETWORK", value: "$2000", benefitKey: "annualMaximum" }
 *       ]
 *     });
 *   };
 *
 *   return <button onClick={handleBatchSave}>Save All</button>;
 * }
 * ```
 */
export const useBatchUpdateCoverage = (
  options?: UseMutationOptions<
    BatchUpdateCoverageResponse,
    Error,
    BatchUpdateCoverageRequest
  >,
): UseMutationResult<
  BatchUpdateCoverageResponse,
  Error,
  BatchUpdateCoverageRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { showError } = useNotification();

  // Extract custom onError to compose with default error handling
  const { onError: customOnError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (request: BatchUpdateCoverageRequest) =>
      batchUpdateCoverage(request, axiosInstance),
    onSuccess: (response, variables) => {
      // Update cache directly instead of invalidating
      if (response.coverage && variables.coverageType) {
        // Transform coverage from backend format to frontend format before caching
        const transformedCoverage = transformSingleCoverage(response.coverage);
        updateCoverageInCache(
          queryClient,
          variables.coverageId,
          variables.coverageType,
          transformedCoverage,
        );
      }
    },
    onError: (error, variables, onMutateResult, context) => {
      const errorMessage = extractErrorMessage(error);
      showError(errorMessage, false); // false = requires manual dismissal
      if (customOnError) {
        customOnError(error, variables, onMutateResult, context);
      }
    },
    ...restOptions,
  });
};

export const useDownloadDocumentFile = (): UseMutationResult<
  Blob,
  Error,
  { downloadLink: string }
> => {
  const axiosInstance = useApiClient();
  const { showError } = useNotification();

  if (!axiosInstance) {
    throw new Error("Download Document API is not mocked");
  }

  return useMutation({
    mutationFn: ({ downloadLink }: { downloadLink: string }) =>
      downloadDocumentFile(downloadLink, axiosInstance),
    onError: (error) => {
      showError(extractErrorMessage(error), false);
    },
  });
};

/**
 * Hook to save complete RFP
 *
 * Updates the query cache directly on success to avoid refetching.
 * Displays error toast notifications on failure requiring manual dismissal.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function RfpSaveButton({ rfpData }: { rfpData: SaveRfpRequest }) {
 *   const mutation = useSaveRfp({
 *     onSuccess: (response) => {
 *       console.log("Saved at:", response.lastModified);
 *     }
 *   });
 *
 *   return (
 *     <button
 *       onClick={() => mutation.mutate(rfpData)}
 *       disabled={mutation.isPending}
 *     >
 *       {mutation.isPending ? "Saving..." : "Save RFP"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useSaveRfp = (
  options?: UseMutationOptions<SaveRfpResponse, Error, SaveRfpRequest>,
): UseMutationResult<SaveRfpResponse, Error, SaveRfpRequest> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { showError } = useNotification();

  // Extract custom onError to compose with default error handling
  const {
    onError: customOnError,
    onSuccess: customOnSuccess,
    ...restOptions
  } = options ?? {};

  return useMutation({
    mutationFn: (request: SaveRfpRequest) => saveRfp(request, axiosInstance),
    onSuccess: (response, variables, onMutateResult, context) => {
      // Update RFP cache with the saved coverages
      updateRfpCoveragesInCache(
        queryClient,
        variables.rfpId,
        variables.coverages,
      );

      variables.coverages.forEach((coverage) => {
        if (coverage.type) {
          // refetchType: 'none' marks stale without an immediate background refetch,
          // preventing a race where stale data overwrites in-progress edits (XTRM-1736).
          queryClient.invalidateQueries({
            queryKey: queryKeys.coverage({
              coverageType: coverage.type,
              rfpId: variables.rfpId,
            }),
            refetchType: "none",
          });
        }

        // Invalidate rate schedule queries for dirty benefit plans.
        // The backend deletes and recreates rate schedules on plan update,
        // so cached rate schedule IDs become stale.
        const dirtyIds = coverage._dirtyPlanIds ?? [];
        for (const planId of dirtyIds) {
          queryClient.invalidateQueries({
            queryKey: rateQueryKeys.schedules(planId),
          });
        }
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.benefitClasses(variables.rfpId),
      });

      if (customOnSuccess) {
        customOnSuccess(response, variables, onMutateResult, context);
      }
    },
    onError: (error, variables, onMutateResult, context) => {
      const errorMessage = extractErrorMessage(error);
      showError(errorMessage, false); // false = requires manual dismissal
      if (customOnError) {
        customOnError(error, variables, onMutateResult, context);
      }
    },
    ...restOptions,
  });
};

/**
 * Hook to update notes for an RFP
 *
 * Automatically invalidates related queries on success.
 * Displays error toast notifications on failure requiring manual dismissal.
 *
 * @param rfpId - RFP ID for cache invalidation
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function RFPNoteDrawer({ rfpId }: { rfpId: string }) {
 *   const mutation = useUpsertRfpNote(rfpId);
 *
 *   const handleSave = async () => {
 *     try {
 *       await mutation.mutateAsync({ notes: noteValue });
 *       // Success - close drawer
 *       onClose();
 *     } catch {
 *       // Error is displayed via toast notification
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleSave} disabled={mutation.isPending}>
 *       {mutation.isPending ? "Saving..." : "Save"}
 *     </Button>
 *   );
 * }
 * ```
 */
export const useUpsertRfpNote = (
  rfpId: string,
): UseMutationResult<RfpNotePayload, Error, RfpNotePayload> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { showError } = useNotification();

  if (!axiosInstance) {
    throw new Error("No API client available for updating RFP note");
  }

  return useMutation({
    mutationFn: (request: RfpNotePayload) =>
      upsertRfpNote(rfpId, request, axiosInstance),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpNotes(rfpId),
      });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      showError(errorMessage, false); // false = requires manual dismissal
    },
  });
};

/**
 * Hook to update notes for a coverage
 *
 * Automatically invalidates related queries on success.
 * Displays error toast notifications on failure requiring manual dismissal.
 *
 * @param rfpId - RFP ID for cache invalidation
 * @param coverageId - Coverage ID for the API call
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function CoverageNoteEditor({ rfpId, coverageId }: { rfpId: string; coverageId: string }) {
 *   const mutation = useUpsertCoverageNote(rfpId, coverageId);
 *
 *   const handleSave = async () => {
 *     try {
 *       await mutation.mutateAsync(noteValue);
 *       // Success - close editor
 *       onClose();
 *     } catch {
 *       // Error is displayed via toast notification
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleSave} disabled={mutation.isPending}>
 *       {mutation.isPending ? "Saving..." : "Save"}
 *     </Button>
 *   );
 * }
 * ```
 */
export const useUpsertCoverageNote = (
  rfpId: string,
  coverageId: string,
): UseMutationResult<RfpNotePayload, Error, string> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { showError } = useNotification();

  if (!axiosInstance) {
    throw new Error("No API client available for updating coverage note");
  }

  return useMutation({
    mutationFn: (notes: string) =>
      upsertCoverageNote(rfpId, coverageId, notes, axiosInstance),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.coverageNotes(rfpId),
      });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      showError(errorMessage, false); // false = requires manual dismissal
    },
  });
};
